import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Trash2, Upload } from "lucide-react";
import {
  api,
  type AudioEntry,
  type AudioGroup,
  type AudioInventory,
} from "@/lib/api";
import { dateTime } from "@/lib/format";
import {
  AudioPlayer,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Empty,
  ErrorNote,
  Input,
  Notice,
  SortableTh,
  Spinner,
  Stat,
  Table,
  TableFooter,
  Td,
  Th,
  Toolbar,
} from "@/components/ui";
import { useClientTable } from "@/lib/table";

/**
 * The expected-against-present matrix.
 *
 * The legacy page listed a fixed grid — every user times every day, month,
 * hour, quarter hour, department and position — whether or not any
 * announcement used those blocks, so a company that never announces an end
 * time was told it was missing several hundred recordings. Here the expected
 * names come from the blueprints the company actually offers.
 */
export function AudioFiles({ companyId }: { companyId: number }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [removing, setRemoving] = useState<AudioEntry | null>(null);

  const key = ["company", companyId, "audio"];
  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => api<AudioInventory>(`/api/admin/companies/${companyId}/audio`),
  });

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const form = new FormData();
      for (const file of files) form.append("files", file, file.name);

      const res = await fetch(`/api/admin/companies/${companyId}/audio`, {
        method: "POST",
        body: form,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrf() },
      });
      if (!res.ok) throw new Error(await res.text());
      return (await res.json()) as { stored: AudioEntry[]; refused: string[] };
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });

  // The matrix above is worked out per request and is therefore already
  // current. What this asks for is the background scan: the per-person state
  // the user list shows, and the count on the dashboard. An upload queues one
  // by itself, so the button is for recordings that arrived some other way.
  const scan = useMutation({
    mutationFn: () =>
      api<{ status: string }>(`/api/admin/companies/${companyId}/audio/scan`, {
        method: "POST",
      }),
  });

  const remove = useMutation({
    mutationFn: (entry: AudioEntry) =>
      api<void>(
        `/api/admin/companies/${companyId}/audio/${encodeURIComponent(fileName(entry))}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      setRemoving(null);
      void qc.invalidateQueries({ queryKey: key });
    },
  });

  const orphans = useClientTable(data?.orphans, {
    initial: { key: "name", dir: "asc" },
    pageSize: 25,
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;
  if (!data) return null;

  const needle = filter.trim().toLowerCase();
  const groups = data.groups
    .map((g) => ({
      ...g,
      entries: g.entries.filter(
        (e) =>
          (!onlyMissing || !e.present) &&
          (!needle || e.name.toLowerCase().includes(needle)),
      ),
    }))
    .filter((g) => g.entries.length > 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Erwartet" value={data.expected} />
        <Stat
          label="Vorhanden"
          value={data.present}
          tone={data.missing === 0 ? "success" : "neutral"}
        />
        <Stat
          label="Fehlend"
          value={data.missing}
          tone={data.missing > 0 ? "warning" : "success"}
        />
      </div>

      {data.truncated && (
        <Notice tone="warning">
          Die Liste wurde gekürzt: die Bausteine dieser Firma ergeben mehr
          Namen, als sinnvoll darstellbar sind. Einschränken hilft.
        </Notice>
      )}

      <Card className="p-4">
        <Toolbar>
          <Input
            className="w-64"
            placeholder="Dateiname suchen"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={onlyMissing}
              onChange={(e) => setOnlyMissing(e.target.checked)}
            />
            nur fehlende
          </label>
          <div className="flex-1" />
          <input
            ref={fileInput}
            type="file"
            accept=".mp3,.wav,audio/mpeg,audio/wav"
            multiple
            hidden
            onChange={(e) => {
              // The list has to be copied before the input is reset: the
              // upload runs asynchronously, and clearing the input empties
              // the FileList it would otherwise read from.
              const files = Array.from(e.target.files ?? []);
              e.target.value = "";
              if (files.length > 0) upload.mutate(files);
            }}
          />
          <Button onClick={() => scan.mutate()} disabled={scan.isPending}>
            <RefreshCw className="size-4" aria-hidden />
            {scan.isPending ? "wird eingereiht…" : "Aufnahmen prüfen"}
          </Button>
          <Button
            variant="primary"
            onClick={() => fileInput.current?.click()}
            disabled={upload.isPending}
          >
            <Upload className="size-4" aria-hidden />
            {upload.isPending ? "wird hochgeladen…" : "Aufnahmen hochladen"}
          </Button>
        </Toolbar>

        {scan.error && <ErrorNote>{(scan.error as Error).message}</ErrorNote>}
        {scan.isSuccess && (
          <div className="mb-3">
            <Notice tone="success">
              Die Prüfung läuft im Hintergrund. Der Aufnahme-Status in der
              Benutzerliste wird in den nächsten Minuten aktualisiert.
            </Notice>
          </div>
        )}

        {upload.error && <ErrorNote>{(upload.error as Error).message}</ErrorNote>}
        {upload.data && (
          <div className="mb-3 flex flex-col gap-2">
            {upload.data.stored.length > 0 && (
              <Notice tone="success">
                {upload.data.stored.length} Aufnahmen gespeichert. Der
                Aufnahme-Status wird gleich im Hintergrund neu geprüft.
              </Notice>
            )}
            {upload.data.refused.length > 0 && (
              <Notice tone="warning">
                <p className="font-medium">Nicht übernommen:</p>
                <ul className="mt-1 list-inside list-disc">
                  {upload.data.refused.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </Notice>
            )}
          </div>
        )}

        {groups.length === 0 ? (
          <Empty
            title={onlyMissing ? "Nichts fehlt" : "Keine Bausteine in Verwendung"}
            hint={
              onlyMissing
                ? "Diese Firma hat alle erwarteten Aufnahmen."
                : "Die Firma bietet noch keine Ansagen an, aus denen sich Bausteine ergeben."
            }
          />
        ) : (
          groups.map((g) => (
            <GroupTable
              key={g.blockId}
              group={g}
              companyId={companyId}
              onRemove={setRemoving}
            />
          ))
        )}
      </Card>

      {data.orphans.length > 0 && (
        <Card className="p-4">
          <h3 className="mb-1 text-sm font-medium">
            Dateien ohne Verwendung ({data.orphans.length})
          </h3>
          <p className="mb-3 text-sm muted">
            Aufnahmen, die kein Bauplan dieser Firma anfordert. Das ist kein
            Fehler — oft sind es Reserven oder ein vertippter Name.
          </p>
          <Table>
            <thead>
              <tr>
                <SortableTh table={orphans} sortKey="name">
                  Datei
                </SortableTh>
                <SortableTh table={orphans} sortKey="modTime" first="desc">
                  Geändert
                </SortableTh>
                <Th>Anhören</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {orphans.rows.map((e) => (
                <tr key={e.key}>
                  <Td className="font-mono text-xs">{e.name}</Td>
                  <Td className="muted">{dateTime(e.modTime)}</Td>
                  <Td>
                    <AudioPlayer src={`/api/media/raw/${companyId}/${fileName(e)}`} />
                  </Td>
                  <Td className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(e)}>
                      <Trash2 className="size-4" aria-hidden />
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <TableFooter table={orphans} noun="Dateien" />
        </Card>
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Aufnahme löschen"
        message={
          <>
            <strong className="font-mono">{removing?.name}</strong> wird gelöscht.
            Ansagen, die diesen Baustein verwenden, fallen danach auf die
            generische Aufnahme zurück oder lassen sich nicht mehr einspielen.
          </>
        }
        onConfirm={() => removing && remove.mutate(removing)}
        onClose={() => setRemoving(null)}
        busy={remove.isPending}
      />
    </div>
  );
}

/**
 * One building block's recordings.
 *
 * It is a component rather than a loop body because each group sorts and
 * pages on its own, and a hook cannot be called from inside a map.
 */
function GroupTable({
  group,
  companyId,
  onRemove,
}: {
  group: AudioGroup;
  companyId: number;
  onRemove: (entry: AudioEntry) => void;
}) {
  const table = useClientTable(group.entries, {
    // Missing first: this page exists to find the gaps.
    initial: { key: "present", dir: "asc" },
    pageSize: 25,
    accessors: { modTime: (e) => e.modTime ?? "" },
  });

  return (
    <section className="mb-5">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-medium">
        {group.description || group.pattern}
        <span className="muted font-normal">{group.pattern}</span>
        {group.missing > 0 ? (
          <Badge tone="warning">{group.missing} fehlen</Badge>
        ) : (
          <Badge tone="success">vollständig</Badge>
        )}
      </h3>

      <Table>
        <thead>
          <tr>
            <SortableTh table={table} sortKey="name">
              Datei
            </SortableTh>
            <SortableTh table={table} sortKey="present">
              Zustand
            </SortableTh>
            <SortableTh table={table} sortKey="modTime" first="desc">
              Geändert
            </SortableTh>
            <Th>Anhören</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {table.rows.map((e) => (
            <tr key={e.name}>
              <Td className="font-mono text-xs">{e.name}</Td>
              <Td>
                {e.present ? (
                  <Badge tone="success">vorhanden</Badge>
                ) : (
                  <Badge tone="warning">fehlt</Badge>
                )}
              </Td>
              <Td className="muted">{e.present ? dateTime(e.modTime) : "—"}</Td>
              <Td>
                {e.present && (
                  <AudioPlayer src={`/api/media/raw/${companyId}/${fileName(e)}`} />
                )}
              </Td>
              <Td className="text-right">
                {e.present && (
                  <Button size="sm" variant="ghost" onClick={() => onRemove(e)}>
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      <TableFooter table={table} noun="Aufnahmen" />
    </section>
  );
}

/** The stored key carries the extension; the name in the matrix does not. */
function fileName(entry: AudioEntry): string {
  if (entry.key) {
    const slash = entry.key.lastIndexOf("/");
    return slash >= 0 ? entry.key.slice(slash + 1) : entry.key;
  }
  return `${entry.name}.mp3`;
}

function csrf(): string {
  const match = document.cookie.match(/(?:^|;\s*)mbm_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}
